import type { Invoice, Subscription } from '../types/subscriptionManagement'
import {
  formatYmdLocal,
  isPaidInvoiceStatus,
  parseYmdLocal,
} from './subscriptionInvoiceCoverage'

export { isPaidInvoiceStatus }

function daysInMonth(y: number, m0: number): number {
  return new Date(y, m0 + 1, 0).getDate()
}

/** Anchor calendar day from contract start (1–31); same day used in other months (clamped). */
function anchorDayFromStart(start: Date): number {
  return start.getDate()
}

/**
 * Same calendar anchor day in a given (year, monthIndex), clamped to month length
 * (e.g. anchor 31 → Feb 28/29).
 */
function anchorInMonth(y: number, monthIndex: number, anchorDay: number): Date {
  const dim = daysInMonth(y, monthIndex)
  const d = Math.min(anchorDay, dim)
  return new Date(y, monthIndex, d)
}

/** Add whole months from base's Y/M, then set day to min(anchorDay, daysInTargetMonth). */
function addMonthsAnchored(base: Date, monthsToAdd: number, anchorDay: number): Date {
  const t = new Date(base.getFullYear(), base.getMonth() + monthsToAdd, 1)
  const dim = daysInMonth(t.getFullYear(), t.getMonth())
  t.setDate(Math.min(anchorDay, dim))
  return t
}

/**
 * Smallest monthly billing period end (anchor day each month) on or after `inv`.
 * @deprecated Legacy renewal mapping — use invoice subscriptionEndDate when available.
 */
function monthlyPeriodEndOnOrAfter(inv: Date, anchorDay: number): Date {
  let y = inv.getFullYear()
  let m = inv.getMonth()
  let end = anchorInMonth(y, m, anchorDay)
  if (end.getTime() >= inv.getTime()) return end
  if (m === 11) {
    y += 1
    m = 0
  } else {
    m += 1
  }
  return anchorInMonth(y, m, anchorDay)
}

/**
 * Smallest period end at contractStart + n·step months (n ≥ 1) on or after `inv`.
 * @deprecated Legacy renewal mapping — use invoice subscriptionEndDate when available.
 */
function steppedPeriodEndOnOrAfter(
  inv: Date,
  contractStart: Date,
  anchorDay: number,
  stepMonths: number,
): Date | null {
  if (stepMonths < 1) return null
  const cap = 800
  for (let n = 1; n <= cap; n += 1) {
    const end = addMonthsAnchored(contractStart, stepMonths * n, anchorDay)
    if (end.getTime() >= inv.getTime()) return end
  }
  return null
}

/** @deprecated Legacy renewal mapping — use invoice subscriptionEndDate when available. */
function periodEndOnOrAfter(
  inv: Date,
  contractStart: Date,
  anchorDay: number,
  billingCycle: string,
): Date | null {
  const c = String(billingCycle || '').trim().toLowerCase()
  if (c === 'annual') {
    return steppedPeriodEndOnOrAfter(inv, contractStart, anchorDay, 12)
  }
  if (c === 'quarterly') {
    return steppedPeriodEndOnOrAfter(inv, contractStart, anchorDay, 3)
  }
  return monthlyPeriodEndOnOrAfter(inv, anchorDay)
}

/**
 * Legacy paid-invoice renewal candidate: anchor-based period end from invoice date
 * and subscription contract start + billing cycle.
 */
function legacyRenewalCandidateFromInvoice(
  inv: Invoice,
  sub: Subscription,
  contractStart: Date,
): Date | null {
  const invD = parseYmdLocal(inv.invoiceDate)
  if (!invD) return null
  const invUse =
    invD.getTime() < contractStart.getTime() ? contractStart : invD
  const anchorDay = anchorDayFromStart(contractStart)
  return periodEndOnOrAfter(
    invUse,
    contractStart,
    anchorDay,
    sub.billingCycle || '',
  )
}

/**
 * Renewal candidate for one paid invoice.
 * 1. Coverage-based: valid subscriptionEndDate on the invoice.
 * 2. Legacy fallback: invoice-date + billing-cycle anchor mapping when coverage is empty.
 */
function renewalCandidateFromPaidInvoice(
  inv: Invoice,
  sub: Subscription,
  contractStart: Date | null,
): Date | null {
  const coverageEnd = parseYmdLocal(inv.subscriptionEndDate)
  if (coverageEnd) return coverageEnd

  if (!contractStart) return null
  return legacyRenewalCandidateFromInvoice(inv, sub, contractStart)
}

/**
 * Effective displayed renewal date for a subscription.
 *
 * - Only **Paid** invoices contribute (Unpaid, Overdue, Cancelled, Draft ignored).
 * - Prefer each paid invoice's `subscriptionEndDate` when set.
 * - Otherwise fall back to legacy anchor-based mapping per invoice.
 * - Result = latest valid candidate across paid invoices, else stored `renewalDate`.
 */
export function effectiveRenewalDate(
  sub: Subscription,
  allInvoices: Invoice[],
): string {
  const stored = String(sub.renewalDate || '').trim()

  const paid = allInvoices.filter(
    i => i.subscriptionId === sub.id && isPaidInvoiceStatus(i.paymentStatus),
  )
  if (paid.length === 0) return stored

  const contractStart = parseYmdLocal(sub.startDate)

  let best: Date | null = null
  for (const inv of paid) {
    const candidate = renewalCandidateFromPaidInvoice(inv, sub, contractStart)
    if (!candidate) continue
    if (!best || candidate.getTime() > best.getTime()) best = candidate
  }

  if (!best) return stored
  return formatYmdLocal(best)
}

/** For sorting subscription rows by displayed renewal. */
export function effectiveRenewalSortTime(
  sub: Subscription,
  allInvoices: Invoice[],
): number {
  const ymd = effectiveRenewalDate(sub, allInvoices)
  const d = parseYmdLocal(ymd)
  return d ? d.getTime() : 0
}
